// src/pages/admin/tabs/properties/ForeignPropertyEditPage.jsx

import React from 'react';
import { useParams, useNavigate, Link } from 'react-router-dom';
import { AdminAPI } from '../../../../api/admin';
import { PublicAPI } from '../../../../api/public';
import { Card, Row2, Row3, Field, Actions, Btn, Collapse } from '../../ui';
import RichText from '../../components/RichText';
import PropertyMediaManager from '../../components/PropertyMediaManager';
import UnitsPanel from '../parts/UnitsPanel';
import ManagersPanel from '../parts/ManagersPanel';
import PaymentPlansPanel from '../parts/PaymentPlansPanel';
import AmenitiesPanel from '../parts/AmenitiesPanel';
import MapPicker from '../../components/MapPicker';

// slugify
const slugify = (s = '') =>
  s.toString()
    .normalize('NFKD')
    .replace(/[\u0300-\u036f]/g, '')
    .toLowerCase()
    .replace(/[^a-z0-9]+/g, '-')
    .replace(/(^-|-$)+/g, '')
    .slice(0, 80);

// валидация
function validate(form) {
  const errors = {};
  if (!(form.title_ru && form.title_ru.trim()) && !(form.title_en && form.title_en.trim())) {
    errors.title_ru = 'Укажите название (RU или EN)';
  }
  if (form.price === '' || form.price === null || form.price === undefined || Number(form.price) < 0) errors.price = 'Укажите цену';
  if (!form.property_type_id) errors.property_type_id = 'Укажите тип объекта';
  if (!form.deal_type_id) errors.deal_type_id = 'Укажите тип сделки';
  if (!form.region_id) errors.region_id = 'Выберите регион';
  if (!form.district_id) errors.district_id = 'Выберите район';
  if (!form.address) errors.address = 'Укажите адрес';
  // Проверка диапазона координат, если заданы
  if (form.lat && (Number(form.lat) < -90 || Number(form.lat) > 90)) errors.lat = 'lat должен быть от -90 до 90';
  if (form.lng && (Number(form.lng) < -180 || Number(form.lng) > 180)) errors.lng = 'lng должен быть от -180 до 180';
  return errors;
}
const hasErrors = (errs) => Object.keys(errs).length > 0;

export default function ForeignPropertyEditPage() {
  const { id } = useParams(); // "new" или число
  const isNew = !id || id === 'new';
  const nav = useNavigate();

  const goToEdit = (propId) => nav(`/admin/foreign/${propId}`);

  const [regions, setRegions] = React.useState([]);
  const [districts, setDistricts] = React.useState([]);
  const [propertyTypes, setPropertyTypes] = React.useState([]);
  const [dealTypes, setDealTypes] = React.useState([]);

  const empty = {
    id: null,
    title_ru: '',
    title_en: '',
    title_zh: '',
    slug: '',
    price: '',
    price_currency: 'USD',
    price_max: '',
    price_per_m2_min: '',
    price_per_m2_max: '',
    // локация
    address: '',
    lat: '',
    lng: '',
    // застройщик
    developer_name_ru: '',
    developer_name_en: '',
    region_id: '',
    district_id: '',
    property_type_id: '',
    deal_type_id: '',
    bedrooms_min: '',
    bedrooms_max: '',
    area_total_min: '',
    area_total_max: '',
    installment_available: false,
    handover_quarter: '',
    handover_year: '',
    is_featured: false,
    is_investment: false,
    is_homepage: false,
    is_active: true,
    status: 'active',
    description_ru: '',
    description_en: '',
    description_zh: '',
    developer_name_ru: '',
    developer_name_en: '',
    developer_name_zh: '',
    meta_title_ru: '',
    meta_title_en: '',
    meta_title_zh: '',
    meta_description_ru: '',
    meta_description_en: '',
    meta_description_zh: '',
  };
  const [form, setForm] = React.useState(empty);
  const [ranges, setRanges] = React.useState({ bedrooms: { min: null, max: null }, area: { min: null, max: null } });
  const [msg, setMsg] = React.useState('');
  const [okToast, setOkToast] = React.useState('');
  const [saving, setSaving] = React.useState(false);
  const [savedModal, setSavedModal] = React.useState(false);
  const [confirmOpen, setConfirmOpen] = React.useState(false);
  const [loadingForm, setLoadingForm] = React.useState(false);

  // валидация
  const [touched, setTouched] = React.useState({});
  const [submitted, setSubmitted] = React.useState(false);
  const errors = validate(form);
  const showErr = (name) => !!errors[name] && (touched[name] || submitted);
  const markTouched = (name) => setTouched((t) => ({ ...t, [name]: true }));

  // refs
  const refs = {
    title_ru: React.useRef(null),
    title_en: React.useRef(null),
    slug: React.useRef(null),
    price: React.useRef(null),
    property_type_id: React.useRef(null),
    deal_type_id: React.useRef(null),
    region_id: React.useRef(null),
    district_id: React.useRef(null),
    address: React.useRef(null),
    lat: React.useRef(null),
    lng: React.useRef(null),
  };
  const errStyle = (name) =>
    showErr(name)
      ? {
          borderColor: '#e66',
          boxShadow: '0 0 0 3px rgba(230,102,102,.15)',
          background: 'rgba(230,102,102,.05)',
        }
      : {};

  // словари + валюты
  const [currencies, setCurrencies] = React.useState([]);

  React.useEffect(() => {
    (async () => {
      try {
        const [rResp, dResp, ptResp, dtResp, navResp] = await Promise.all([
          PublicAPI.regions(),
          PublicAPI.districts(),
          PublicAPI.propertyTypes(),
          PublicAPI.dealTypes(),
          PublicAPI.navigation(),
        ]);
        setRegions(rResp?.regions || []);
        setDistricts(dResp?.districts || dResp?.items || []);
        setPropertyTypes(ptResp?.property_types || []);
        setDealTypes(dtResp?.deal_types || []);
        setCurrencies(navResp?.currencies || []);
      } catch (e) {
        console.error('Failed to load dictionaries', e);
        setRegions([]);
        setDistricts([]);
        setPropertyTypes([]);
        setDealTypes([]);
        setCurrencies([]);
      }
    })();
  }, []);

  // автогенерация slug
  React.useEffect(() => {
    if (isNew) return; // при создании slug генерирует бэкенд
    const src = form.title_ru || form.title_en;
    if (src && !touched.slug && !form.slug) {
      setForm((f) => ({ ...f, slug: slugify(src) }));
    }
  }, [isNew, form.title_ru, form.title_en, form.slug, touched.slug]);

  // загрузка текущего объекта (редактирование)
  React.useEffect(() => {
    (async () => {
      if (isNew) return;
      setLoadingForm(true);
      setMsg('');
      try {
        const resp = await AdminAPI.getForeignProperty(Number(id), { locale: 'ru' });
        const p = resp?.property || resp || {};

        const loadedCurrency = p.price_currency || p.currency?.code || 'USD';

        setForm({
          id: p.id ?? null,
          title_ru: p.title_ru ?? p.title ?? '',
          title_en: p.title_en ?? '',
          title_zh: p.title_zh ?? '',
          slug: p.slug ?? '',
          price: p.price ?? p.price_base ?? p.price_aed ?? '',
          price_currency: loadedCurrency,
          price_max: p.price_max ?? '',
          price_per_m2_min: p.price_per_m2_min ?? '',
          price_per_m2_max: p.price_per_m2_max ?? '',
          // локация
          address: p.address ?? p.city ?? '',
          lat: p.lat ?? '',
          lng: p.lng ?? '',
          developer_name_ru: p.developer_name_ru ?? p.developer ?? '',
          developer_name_en: p.developer_name_en ?? '',
          region_id: p.region_id ?? p.region?.id ?? '',
          district_id: p.district_id ?? p.district?.id ?? '',
          property_type_id: p.property_type_id ?? p.property_type?.id ?? '',
          deal_type_id: p.deal_type_id ?? p.deal_type?.id ?? '',
          bedrooms_min: (p.bedrooms_min != null ? p.bedrooms_min : (p.bedrooms_range?.min ?? '')),
          bedrooms_max: (p.bedrooms_max != null ? p.bedrooms_max : (p.bedrooms_range?.max ?? '')),
          area_total_min: (p.area_total_min != null ? p.area_total_min : (p.area_total_range?.min ?? '')),
          area_total_max: (p.area_total_max != null ? p.area_total_max : (p.area_total_range?.max ?? '')),
          installment_available: !!p.installment_available,
          handover_quarter: p.handover_quarter ?? '',
          handover_year: p.handover_year ?? '',
          is_featured: !!p.is_featured,
          is_investment: !!p.is_investment,
          is_homepage: !!p.is_homepage,
          is_active: p.is_active ?? true,
          status: p.status || 'active',
          description_ru: p.description_ru ?? p.description ?? '',
          description_en: p.description_en ?? '',
          description_zh: p.description_zh ?? '',
          developer_name_ru: p.developer_name_ru ?? p.developer ?? '',
          developer_name_en: p.developer_name_en ?? '',
          developer_name_zh: p.developer_name_zh ?? '',
          meta_title_ru: p.meta_title_ru ?? '',
          meta_title_en: p.meta_title_en ?? '',
          meta_title_zh: p.meta_title_zh ?? '',
          meta_description_ru: p.meta_description_ru ?? '',
          meta_description_en: p.meta_description_en ?? '',
          meta_description_zh: p.meta_description_zh ?? '',
        });

        setRanges({
          bedrooms: {
            min: (p?.bedrooms_range && (p.bedrooms_range.min ?? null)) ?? null,
            max: (p?.bedrooms_range && (p.bedrooms_range.max ?? null)) ?? null,
          },
          area: {
            min: (p?.area_total_range && (p.area_total_range.min ?? null)) ?? null,
            max: (p?.area_total_range && (p.area_total_range.max ?? null)) ?? null,
          },
        });

        setTouched({});
        setSubmitted(false);
      } catch (e) {
        setMsg(e.message || 'Не удалось загрузить объект');
      } finally {
        setLoadingForm(false);
      }
    })();
  }, [isNew, id]);
  const toPayload = () => {
    return {
      title_ru: form.title_ru || undefined,
      title_en: form.title_en || undefined,
      title_zh: form.title_zh || undefined,
      slug: isNew ? undefined : (form.slug || undefined),
      price: form.price ? Number(form.price) : undefined,
      price_currency: form.price_currency || 'USD',
      price_max: form.price_max ? Number(form.price_max) : null,
      price_per_m2_min: form.price_per_m2_min ? Number(form.price_per_m2_min) : null,
      price_per_m2_max: form.price_per_m2_max ? Number(form.price_per_m2_max) : null,

      // важно: сервер ждёт address, lat/lng
      address: form.address || undefined,
      lat: form.lat !== '' ? Number(form.lat) : undefined,
      lng: form.lng !== '' ? Number(form.lng) : undefined,

      // застройщик: если заполнено только одно поле — отправим developer (в оба языка)
      ...(form.developer_name_ru && form.developer_name_en
        ? { developer_name_ru: form.developer_name_ru, developer_name_en: form.developer_name_en }
        : form.developer_name_ru
          ? { developer: form.developer_name_ru }
          : form.developer_name_en
            ? { developer: form.developer_name_en }
            : {}),
      region_id: form.region_id ? Number(form.region_id) : undefined,
      district_id: form.district_id ? Number(form.district_id) : undefined,
      property_type_id: form.property_type_id ? Number(form.property_type_id) : undefined,
      deal_type_id: form.deal_type_id ? Number(form.deal_type_id) : undefined,
      bedrooms_min: form.bedrooms_min !== '' ? Number(form.bedrooms_min) : undefined,
      bedrooms_max: form.bedrooms_max !== '' ? Number(form.bedrooms_max) : undefined,
      area_total_min: form.area_total_min !== '' ? Number(form.area_total_min) : undefined,
      area_total_max: form.area_total_max !== '' ? Number(form.area_total_max) : undefined,
      installment_available: !!form.installment_available,
      handover_quarter: form.handover_quarter || undefined,
      handover_year: form.handover_year ? Number(form.handover_year) : undefined,
      is_featured: !!form.is_featured,
      is_investment: !!form.is_investment,
      is_active: !!form.is_active,
      status: form.status || undefined,
      description_ru: form.description_ru || undefined,
      description_en: form.description_en || undefined,
      description_zh: form.description_zh || undefined,
      developer_name_ru: form.developer_name_ru || undefined,
      developer_name_en: form.developer_name_en || undefined,
      developer_name_zh: form.developer_name_zh || undefined,
      meta_title_ru: form.meta_title_ru || undefined,
      meta_title_en: form.meta_title_en || undefined,
      meta_title_zh: form.meta_title_zh || undefined,
      meta_description_ru: form.meta_description_ru || undefined,
      meta_description_en: form.meta_description_en || undefined,
      meta_description_zh: form.meta_description_zh || undefined,
    };
  };

  const scrollToFirstError = () => {
    const order = ['title_ru','title_en','slug','price','property_type_id','deal_type_id','region_id','district_id','address','lat','lng'];
    for (const key of order) {
      if (errors[key] && refs[key]?.current) {
        refs[key].current.scrollIntoView({ behavior: 'smooth', block: 'center' });
        try { refs[key].current.focus(); } catch {}
        break;
      }
    }
  };

  const save = async (e) => {
    e?.preventDefault?.();
    setSubmitted(true);
    setMsg('');

    if (!isNew && (!form.slug || !form.slug.trim()) && form.title?.trim()) {
      // Автогенерация slug для совместимости с текущим бэкендом,
      // если сервер ещё не генерирует его сам
      const auto = slugify(form.title);
      if (auto) setForm((f) => ({ ...f, slug: auto }));
    }

    const errs = validate({ ...form });
    if (hasErrors(errs)) {
      setMsg('Заполните обязательные поля');
      scrollToFirstError();
      return;
    }

    setSaving(true);
    try {
      const payload = toPayload();
      let propertyId = isNew ? null : form.id || Number(id);

      if (isNew) {
        const resp = await AdminAPI.createForeignProperty(payload);
        propertyId = resp?.property?.id || resp?.id;
        if (!propertyId) throw new Error('Не получили ID созданного объекта');
        // применим флаги, зависящие от отдельных эндпоинтов
        try{
          if (form.is_featured) await AdminAPI.setFeaturedForeignProperty(propertyId, true);
          if (form.is_homepage) await AdminAPI.homepageSetForeignProperty(propertyId, true);
        }catch(e){ /* не критично */ }
        setMsg('Объект создан. Открываю менеджер фотографий…');
        goToEdit(propertyId);
        return;
      } else {
        await AdminAPI.updateForeignProperty(propertyId, payload);
        setMsg('Изменения сохранены');
        setOkToast('Изменения сохранены');
        setSavedModal(true);
        setTimeout(()=>{ setOkToast(''); setSavedModal(false); }, 1800);
      }
    } catch (e2) {
      setMsg(e2.message || 'Ошибка сохранения');
    } finally {
      setSaving(false);
    }
  };

  const districtsForForm = form.region_id
    ? districts.filter((d) => String(d.region_id ?? '') === String(form.region_id))
    : districts;

  return (
    <>
      {/* Модальное окно: сохраняем / сохранено */}
      {(saving || savedModal) && (
        <div style={{
          position:'fixed', inset:0, zIndex:4000, background:'rgba(0,0,0,.35)',
          display:'flex', alignItems:'center', justifyContent:'center'
        }} aria-live="polite" role="status">
          <div style={{
            background:'var(--card)', border:'1px solid var(--bdr)', borderRadius:16,
            boxShadow:'var(--shadow)', padding:'18px 20px', minWidth:280, textAlign:'center'
          }}>
            <div style={{fontWeight:800, marginBottom:6}}>
              {saving ? 'Сохраняем…' : 'Сохранено'}
            </div>
            <div style={{opacity:.8, fontSize:13}}>
              {saving ? 'Пожалуйста, подождите' : 'Изменения успешно сохранены'}
            </div>
            {saving && (
              <div style={{marginTop:12, display:'grid', placeItems:'center'}}>
                <div style={{
                  width:28, height:28, border:'3px solid #ccc', borderTopColor:'var(--primary)',
                  borderRadius:'50%', animation:'spin 1s linear infinite'
                }}/>
              </div>
            )}
          </div>
          <style>{'@keyframes spin{from{transform:rotate(0)}to{transform:rotate(360deg)}}'}</style>
        </div>
      )}
      {/* toast подтверждение сохранения */}
      {!!okToast && (
        <div style={{position:'fixed', right:18, top:18, zIndex:3000, pointerEvents:'none'}} role="status" aria-live="polite">
          <div style={{background:'#1b5e20', color:'#fff', padding:'10px 14px', borderRadius:12, boxShadow:'0 8px 20px rgba(0,0,0,.2)'}}>
            {okToast}
          </div>
        </div>
      )}
      {!isNew && (
        <Actions style={{ marginBottom: 12 }}>
          <Link className="btn" to="/admin/foreign">← К списку зарубежной недвижимости</Link>
        </Actions>
      )}

      {/* Основной блок всегда сверху и открыт */}
      <Card as="form" onSubmit={save} style={{ marginTop: 12 }}>
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 16 }}>
          <h3 style={{ margin: 0 }}>
            {isNew ? 'Создать зарубежную недвижимость' : `Редактировать зарубежную недвижимость #${id}`}
            {loadingForm ? ' — загрузка…' : ''}
          </h3>
          {/* Кнопка сохранить в верхней части */}
          <Actions style={{ margin: 0 }}>
            <Btn $kind="primary" type="button" disabled={saving} onClick={()=>setConfirmOpen(true)}>
              {isNew ? 'Создать' : 'Сохранить'}
            </Btn>
          </Actions>
        </div>
        {msg && (
          <div style={{ color: hasErrors(errors) ? '#e66' : 'var(--muted)', marginBottom: 8 }}>
            {msg}
          </div>
        )}

        <Row3>
          <Field>
            Название (RU)
            <input
              ref={refs.title_ru}
              value={form.title_ru}
              onChange={(e) => setForm({ ...form, title_ru: e.target.value })}
              onBlur={() => markTouched('title_ru')}
              aria-invalid={showErr('title_ru')}
              style={errStyle('title_ru')}
            />
            {showErr('title_ru') && <small style={{ color: '#e66' }}>{errors.title_ru}</small>}
          </Field>
          <Field>
            Название (EN)
            <input
              ref={refs.title_en}
              value={form.title_en}
              onChange={(e) => setForm({ ...form, title_en: e.target.value })}
              onBlur={() => markTouched('title_en')}
              aria-invalid={showErr('title_en')}
              style={errStyle('title_en')}
            />
          </Field>
          <Field>
            Название (ZH)
            <input
              value={form.title_zh}
              onChange={(e) => setForm({ ...form, title_zh: e.target.value })}
            />
          </Field>
        </Row3>
        <Row2>
          {!isNew && (
            <Field>
              Slug (опционально)
              <input
                ref={refs.slug}
                value={form.slug}
                onChange={(e) => setForm({ ...form, slug: e.target.value })}
                onBlur={() => markTouched('slug')}
                placeholder="unique-slug"
                aria-invalid={false}
                style={{}}
              />
              <small>Можно оставить пустым — slug сгенерируется автоматически; уникальность проверит сервер.</small>
            </Field>
          )}
        </Row2>

        {/* Застройщик */}
        <Row2>
          <Field>
            Застройщик (RU)
            <input
              value={form.developer_name_ru}
              onChange={(e)=>setForm(f=>({...f, developer_name_ru: e.target.value}))}
              placeholder="напр. ПИК"
            />
            <small>Если заполнено только это поле — будет записано в оба языка</small>
          </Field>
          <Field>
            Застройщик (EN)
            <input
              value={form.developer_name_en}
              onChange={(e)=>setForm(f=>({...f, developer_name_en: e.target.value}))}
              placeholder="e.g. PIK"
            />
          </Field>
        </Row2>

        <Row3>
          <Field>
            Цена от
            <input
              ref={refs.price}
              type="number"
              value={form.price}
              onChange={(e) => setForm({ ...form, price: e.target.value })}
              onBlur={() => markTouched('price')}
              aria-invalid={showErr('price')}
              style={errStyle('price')}
              required
            />
            {showErr('price') && <small style={{ color: '#e66' }}>{errors.price}</small>}
          </Field>
          <Field>
            Цена до
            <input
              type="number"
              value={form.price_max}
              onChange={(e) => setForm({ ...form, price_max: e.target.value })}
              placeholder="Максимальная цена"
            />
            <small>Опционально. Если не указано, будет показана только цена "от"</small>
          </Field>
          <Field>
            Валюта
            <select
              value={form.price_currency}
              onChange={(e)=>setForm(f=>({...f, price_currency: e.target.value}))}
            >
              {currencies.length > 0 ? (
                currencies.map(c => (
                  <option key={c.code} value={c.code}>
                    {c.code} ({c.symbol})
                  </option>
                ))
              ) : (
                <>
                  <option value="USD">USD</option>
                  <option value="EUR">EUR</option>
                  <option value="GBP">GBP</option>
                  <option value="AED">AED</option>
                  <option value="RUB">RUB</option>
                </>
              )}
            </select>
            <small>Цены сохраняются в выбранной валюте</small>
          </Field>
        </Row3>

        <Row3>
          <Field>
            Цена за м² от
            <input
              type="number"
              value={form.price_per_m2_min}
              onChange={(e) => setForm({ ...form, price_per_m2_min: e.target.value })}
              placeholder="Цена за м² от"
            />
            <small>Стоимость за квадратный метр (минимальная)</small>
          </Field>
          <Field>
            Цена за м² до
            <input
              type="number"
              value={form.price_per_m2_max}
              onChange={(e) => setForm({ ...form, price_per_m2_max: e.target.value })}
              placeholder="Цена за м² до"
            />
            <small>Стоимость за квадратный метр (максимальная)</small>
          </Field>
          <Field>
            
          </Field>
        </Row3>

        <Row3>
          <Field>
            Сдача (кв.)
            <input
              value={form.handover_quarter}
              onChange={(e) => setForm({ ...form, handover_quarter: e.target.value })}
              placeholder="Q4"
            />
          </Field>
          <Field>
            Сдача (год)
            <input
              type="number"
              value={form.handover_year}
              onChange={(e) => setForm({ ...form, handover_year: e.target.value })}
              placeholder="2026"
            />
          </Field>
          <Field>
            
          </Field>
        </Row3>

        {/* Диапазоны (опционально, можно переопределить автоматику от юнитов) */}
        <Card>
          <h3>Диапазоны (опционально)</h3>
          <Row3>
            <Field>
              Комнаты от
              <input type="number" value={form.bedrooms_min} onChange={(e)=>setForm(f=>({...f, bedrooms_min: e.target.value}))} />
              <small>
                Текущий агрегат: {`{ min: ${ranges.bedrooms.min ?? '—'}, max: ${ranges.bedrooms.max ?? '—'} }`}
              </small>
            </Field>
            <Field>
              Комнаты до
              <input type="number" value={form.bedrooms_max} onChange={(e)=>setForm(f=>({...f, bedrooms_max: e.target.value}))} />
              <small>
                Текущий агрегат: {`{ min: ${ranges.bedrooms.min ?? '—'}, max: ${ranges.bedrooms.max ?? '—'} }`}
              </small>
            </Field>
            <Field>
              
            </Field>
          </Row3>
          <Row3>
            <Field>
              Площадь от, м²
              <input type="number" value={form.area_total_min} onChange={(e)=>setForm(f=>({...f, area_total_min: e.target.value}))} />
              <small>
                Текущий агрегат: {`{ min: ${ranges.area.min ?? '—'}, max: ${ranges.area.max ?? '—'} }`}
              </small>
            </Field>
            <Field>
              Площадь до, м²
              <input type="number" value={form.area_total_max} onChange={(e)=>setForm(f=>({...f, area_total_max: e.target.value}))} />
              <small>
                Текущий агрегат: {`{ min: ${ranges.area.min ?? '—'}, max: ${ranges.area.max ?? '—'} }`}
              </small>
            </Field>
            <Field>
              
            </Field>
          </Row3>
          <small style={{opacity:.7}}>Если не указывать — диапазоны будут вычисляться автоматически по юнитам.</small>
        </Card>

        <Row3>
          <Field>
            Регион
            <select
              ref={refs.region_id}
              value={form.region_id}
              onChange={(e) => {
                setForm({ ...form, region_id: e.target.value, district_id: '' });
                markTouched('region_id');
              }}
              onBlur={() => markTouched('region_id')}
              aria-invalid={showErr('region_id')}
              style={errStyle('region_id')}
              required
            >
              <option value="">— выберите —</option>
              {regions.map((r) => (
                <option key={r.id} value={r.id}>
                  {r.name || r.title || r.slug}
                </option>
              ))}
            </select>
            {showErr('region_id') && <small style={{ color: '#e66' }}>{errors.region_id}</small>}
          </Field>
          <Field>
            Район
            <select
              ref={refs.district_id}
              value={form.district_id}
              onChange={(e) => {
                setForm({ ...form, district_id: e.target.value });
                markTouched('district_id');
              }}
              onBlur={() => markTouched('district_id')}
              aria-invalid={showErr('district_id')}
              style={errStyle('district_id')}
              required
            >
              <option value="">— выберите —</option>
              {districtsForForm.map((d) => (
                <option key={d.id} value={d.id}>
                  {d.name || d.title || d.slug}
                </option>
              ))}
            </select>
            {showErr('district_id') && (
              <small style={{ color: '#e66' }}>{errors.district_id}</small>
            )}
          </Field>
        
        </Row3>

        {/* Флаги */}
        <Card>
          <h3>Флаги</h3>
          <Row3>
            <Field>Активен
              <div>
                <button type="button" className="btn" onClick={()=>setForm(f=>({...f, is_active:true}))} disabled={form.is_active}
                        style={form.is_active ? {background:'var(--primary)', color:'var(--primaryFg)', borderColor:'transparent'} : undefined}>Вкл</button>{' '}
                <button type="button" className="btn" onClick={()=>setForm(f=>({...f, is_active:false}))} disabled={!form.is_active}
                        style={!form.is_active ? {background:'var(--primary)', color:'var(--primaryFg)', borderColor:'transparent'} : undefined}>Выкл</button>
              </div>
            </Field>
            <Field>Рассрочка
              <div>
                <button type="button" className="btn" onClick={()=>setForm(f=>({...f, installment_available:true}))} disabled={!!form.installment_available}
                        style={form.installment_available ? {background:'var(--primary)', color:'var(--primaryFg)', borderColor:'transparent'} : undefined}>Да</button>{' '}
                <button type="button" className="btn" onClick={()=>setForm(f=>({...f, installment_available:false}))} disabled={!form.installment_available}
                        style={!form.installment_available ? {background:'var(--primary)', color:'var(--primaryFg)', borderColor:'transparent'} : undefined}>Нет</button>
              </div>
            </Field>
            <Field>Инвестиционный
              <div>
                <button type="button" className="btn" onClick={async()=>{
                  if (isNew) { setForm(f=>({...f, is_investment:true})); return; }
                  try{ 
                    // Note: These API methods might not exist for foreign properties yet
                    // await AdminAPI.setInvestmentForeignProperty(Number(id), true); 
                    setForm(f=>({...f, is_investment:true})); 
                    setOkToast('Флаг "Инвестиционный" включён'); 
                    setTimeout(()=>setOkToast(''),2000);
                  }catch(e){
                    console.error('Ошибка установки флага инвестиционный:', e);
                    setMsg(e.message || 'Не удалось установить флаг "Инвестиционный"');
                    setTimeout(()=>setMsg(''), 3000);
                  }
                }} disabled={!!form.is_investment}
                   style={form.is_investment ? 
                     {background:'var(--primary)', color:'var(--primaryFg)', borderColor:'transparent'} : 
                     {background:'#f8f9fa', color:'#6c757d', borderColor:'#dee2e6'}
                   }>Да</button>{' '}
                <button type="button" className="btn" onClick={async()=>{
                  if (isNew) { setForm(f=>({...f, is_investment:false})); return; }
                  try{ 
                    // await AdminAPI.setInvestmentForeignProperty(Number(id), false); 
                    setForm(f=>({...f, is_investment:false})); 
                    setOkToast('Флаг "Инвестиционный" выключён'); 
                    setTimeout(()=>setOkToast(''),2000);
                  }catch(e){
                    console.error('Ошибка снятия флага инвестиционный:', e);
                    setMsg(e.message || 'Не удалось снять флаг "Инвестиционный"');
                    setTimeout(()=>setMsg(''), 3000);
                  }
                }} disabled={!form.is_investment}
                   style={!form.is_investment ? 
                     {background:'var(--primary)', color:'var(--primaryFg)', borderColor:'transparent'} : 
                     {background:'#f8f9fa', color:'#6c757d', borderColor:'#dee2e6'}
                   }>Нет</button>
              </div>
            </Field>
          </Row3>

          <Row3>
            <Field>Рекомендуемый
              <div>
                <button type="button" className="btn" onClick={async()=>{
                  if (isNew) { setForm(f=>({...f, is_featured:true})); return; }
                  try{ 
                    // await AdminAPI.setFeaturedForeignProperty(Number(id), true); 
                    setForm(f=>({...f, is_featured:true})); 
                    setOkToast('Флаг "Рекомендуемый" включён'); 
                    setTimeout(()=>setOkToast(''),2000);
                  }catch(e){
                    console.error('Ошибка установки флага рекомендуемый:', e);
                    setMsg(e.message || 'Не удалось установить флаг "Рекомендуемый"');
                    setTimeout(()=>setMsg(''), 3000);
                  }
                }} disabled={!!form.is_featured}
                   style={form.is_featured ? 
                     {background:'var(--primary)', color:'var(--primaryFg)', borderColor:'transparent'} : 
                     {background:'#f8f9fa', color:'#6c757d', borderColor:'#dee2e6'}
                   }>Да</button>{' '}
                <button type="button" className="btn" onClick={async()=>{
                  if (isNew) { setForm(f=>({...f, is_featured:false})); return; }
                  try{ 
                    // await AdminAPI.setFeaturedForeignProperty(Number(id), false); 
                    setForm(f=>({...f, is_featured:false})); 
                    setOkToast('Флаг "Рекомендуемый" выключён'); 
                    setTimeout(()=>setOkToast(''),2000);
                  }catch(e){
                    console.error('Ошибка снятия флага рекомендуемый:', e);
                    setMsg(e.message || 'Не удалось снять флаг "Рекомендуемый"');
                    setTimeout(()=>setMsg(''), 3000);
                  }
                }} disabled={!form.is_featured}
                   style={!form.is_featured ? 
                     {background:'var(--primary)', color:'var(--primaryFg)', borderColor:'transparent'} : 
                     {background:'#f8f9fa', color:'#6c757d', borderColor:'#dee2e6'}
                   }>Нет</button>
              </div>
            </Field>

            <Field>Герой (на главной)
              <div>
                <button type="button" className="btn" onClick={async()=>{
                  if (isNew) { setForm(f=>({...f, is_homepage:true})); return; }
                  try{ 
                    // await AdminAPI.homepageSetForeignProperty(Number(id), true); 
                    setForm(f=>({...f, is_homepage:true})); 
                    setOkToast('Добавлен в герои'); 
                    setTimeout(()=>setOkToast(''),2000);
                  }catch(e){
                    console.error('Ошибка установки флага герой:', e);
                    setMsg(e.message || 'Не удалось добавить в герои');
                    setTimeout(()=>setMsg(''), 3000);
                  }
                }} disabled={!!form.is_homepage}
                   style={form.is_homepage ? 
                     {background:'var(--primary)', color:'var(--primaryFg)', borderColor:'transparent'} : 
                     {background:'#f8f9fa', color:'#6c757d', borderColor:'#dee2e6'}
                   }>Да</button>{' '}
                <button type="button" className="btn" onClick={async()=>{
                  if (isNew) { setForm(f=>({...f, is_homepage:false})); return; }
                  try{ 
                    // await AdminAPI.homepageSetForeignProperty(Number(id), false); 
                    setForm(f=>({...f, is_homepage:false})); 
                    setOkToast('Снят из героев'); 
                    setTimeout(()=>setOkToast(''),2000);
                  }catch(e){
                    console.error('Ошибка снятия флага герой:', e);
                    setMsg(e.message || 'Не удалось снять из героев');
                    setTimeout(()=>setMsg(''), 3000);
                  }
                }} disabled={!form.is_homepage}
                   style={!form.is_homepage ? 
                     {background:'var(--primary)', color:'var(--primaryFg)', borderColor:'transparent'} : 
                     {background:'#f8f9fa', color:'#6c757d', borderColor:'#dee2e6'}
                   }>Нет</button>
              </div>
            </Field>

            <Field>Статус
              <select value={form.status} onChange={e=>setForm(f=>({...f, status:e.target.value}))}>
                <option value="active">active</option>
                <option value="reserved">reserved</option>
                <option value="sold">sold</option>
                <option value="archived">archived</option>
              </select>
            </Field>
          </Row3>
        </Card>

        {/* словари */}
        <Row2>
          <Field>
            Тип объекта
            <select
              ref={refs.property_type_id}
              value={form.property_type_id}
              onChange={(e) => setForm({ ...form, property_type_id: e.target.value })}
              onBlur={() => markTouched('property_type_id')}
              aria-invalid={showErr('property_type_id')}
              style={errStyle('property_type_id')}
              required
            >
              <option value="">— выберите —</option>
              {propertyTypes.map((pt) => (
                <option key={pt.id} value={pt.id}>
                  {pt.name} — {pt.slug} [id:{pt.id}]
                </option>
              ))}
            </select>
            {showErr('property_type_id') && (
              <small style={{ color: '#e66' }}>{errors.property_type_id}</small>
            )}
          </Field>

          <Field>
            Тип/вариант сделки
            <select
              ref={refs.deal_type_id}
              value={form.deal_type_id}
              onChange={(e) => setForm({ ...form, deal_type_id: e.target.value })}
              onBlur={() => markTouched('deal_type_id')}
              aria-invalid={showErr('deal_type_id')}
              style={errStyle('deal_type_id')}
              required
            >
              <option value="">— выберите —</option>
              {dealTypes.map((dt) => (
                <option key={dt.id} value={dt.id}>
                  {dt.name} — {dt.slug} [id:{dt.id}]
                </option>
              ))}
            </select>
            {showErr('deal_type_id') && (
              <small style={{ color: '#e66' }}>{errors.deal_type_id}</small>
            )}
          </Field>
        </Row2>

        {/* Локация */}
        <h4 style={{ margin: '18px 0 8px' }}>Локация</h4>
        <Row3>
          <Field>
            Адрес
            <input
              ref={refs.address}
              value={form.address}
              onChange={(e) => setForm({ ...form, address: e.target.value })}
              onBlur={() => markTouched('address')}
              aria-invalid={showErr('address')}
              style={errStyle('address')}
              placeholder="улица, дом, город"
              required
            />
            {showErr('address') && <small style={{ color: '#e66' }}>{errors.address}</small>}
          </Field>
          <Field>
            Широта (lat)
            <input
              ref={refs.lat}
              type="number" step="any"
              value={form.lat}
              onChange={(e) => setForm({ ...form, lat: e.target.value })}
              onBlur={() => markTouched('lat')}
              aria-invalid={showErr('lat')}
              style={errStyle('lat')}
              placeholder="55.751244"
            />
            {showErr('lat') && <small style={{ color: '#e66' }}>{errors.lat}</small>}
          </Field>
          <Field>
            Долгота (lng)
            <input
              ref={refs.lng}
              type="number" step="any"
              value={form.lng}
              onChange={(e) => setForm({ ...form, lng: e.target.value })}
              onBlur={() => markTouched('lng')}
              aria-invalid={showErr('lng')}
              style={errStyle('lng')}
              placeholder="37.618423"
            />
            {showErr('lng') && <small style={{ color: '#e66' }}>{errors.lng}</small>}
          </Field>
        </Row3>

        <MapPicker
          value={{
            lat: form.lat !== '' ? Number(form.lat) : undefined,
            lng: form.lng !== '' ? Number(form.lng) : undefined,
            address: form.address
          }}
          onChange={(v) => {
            setForm((f) => ({
              ...f,
              address: v.address ?? f.address,
              lat: v.lat ?? f.lat,
              lng: v.lng ?? f.lng,
            }));
          }}
          lang="ru_RU"
          height={360}
        />
        {/* Описание */}
        <h4 style={{ margin: '18px 0 8px' }}>Описание</h4>
        <div style={{ display: 'flex', gap: 12, alignItems: 'center', marginBottom: 8 }}>
          <div className="badge">RU</div>
          <button
            type="button"
            className="btn"
            onClick={() => {
              const t = `<h3>Локация</h3><p>Престижное расположение зарубежной недвижимости в развитом районе с отличной инфраструктурой.</p>
<h3>Инфраструктура</h3><ul><li>Международные школы</li><li>Торговые центры и рестораны</li><li>Парки и зоны отдыха</li><li>Транспортная доступность</li></ul>
<h3>Характеристики недвижимости</h3><p>Современная планировка, качественная отделка, панорамные окна.</p>
<h3>Условия покупки</h3><p>Ипотека/рассрочка. Индивидуальные предложения по запросу.</p>`;
              setForm((f) => ({
                ...f,
                description_ru: (f.description_ru || '') + (f.description_ru ? '<br/><br/>' : '') + t,
              }));
            }}
          >
            Вставить шаблон
          </button>
          <button type="button" className="btn" onClick={() => setForm((f) => ({ ...f, description_ru: '' }))}>
            Очистить RU
          </button>
          <button
            type="button"
            className="btn"
            onClick={() => { if (!form.description_en) setForm((f) => ({ ...f, description_en: f.description_ru })); }}
          >
            Скопировать RU → EN
          </button>
        </div>
        <RichText
          value={form.description_ru}
          onChange={(v) => setForm((f) => ({ ...f, description_ru: v }))}
          placeholder="Русское описание…"
        />

        <div style={{ display: 'flex', gap: 12, alignItems: 'center', margin: '14px 0 8px' }}>
          <div className="badge">EN</div>
          <button
            type="button"
            className="btn"
            onClick={() => {
              const t = `<h3>Location</h3><p>Prestigious foreign property location in a developed area with excellent infrastructure.</p>
<h3>Infrastructure</h3><ul><li>International schools</li><li>Shopping centers & restaurants</li><li>Parks & recreation areas</li><li>Transport accessibility</li></ul>
<h3>Property Features</h3><p>Modern layout, quality finishes, panoramic windows.</p>
<h3>Purchase terms</h3><p>Mortgage/installments. Tailored offers on request.</p>`;
              setForm((f) => ({
                ...f,
                description_en: (f.description_en || '') + (f.description_en ? '<br/><br/>' : '') + t,
              }));
            }}
          >
            Insert template
          </button>
          <button type="button" className="btn" onClick={() => setForm((f) => ({ ...f, description_en: '' }))}>
            Clear EN
          </button>
        </div>
        <RichText
          value={form.description_en}
          onChange={(v) => setForm((f) => ({ ...f, description_en: v }))}
          placeholder="English description…"
        />

        {/* SEO блок */}
        <h4 style={{ margin: '18px 0 8px' }}>SEO</h4>
        <Row2>
          <Field>
            Meta title RU
            <input value={form.meta_title_ru} onChange={e=>setForm(f=>({...f, meta_title_ru: e.target.value}))} />
          </Field>
          <Field>
            Meta title EN
            <input value={form.meta_title_en} onChange={e=>setForm(f=>({...f, meta_title_en: e.target.value}))} />
          </Field>
        </Row2>

        <Row2>
          <Field>
            Meta description RU
            <textarea rows={3} value={form.meta_description_ru} onChange={e=>setForm(f=>({...f, meta_description_ru: e.target.value}))} />
          </Field>
          <Field>
            Meta description EN
            <textarea rows={3} value={form.meta_description_en} onChange={e=>setForm(f=>({...f, meta_description_en: e.target.value}))} />
          </Field>
        </Row2>

        <Actions>
          {/* Дублированная кнопка сохранения внизу формы */}
          <Btn $kind="primary" type="button" disabled={saving} onClick={()=>setConfirmOpen(true)}>
            {isNew ? 'Создать' : 'Сохранить'}
          </Btn>
          <Btn type="button" onClick={() => nav('/admin/foreign')}>Отмена</Btn>
        </Actions>
        {!!okToast && (
          <div style={{marginTop:10, color:'#1b5e20'}} aria-live="polite">{okToast}</div>
        )}
      </Card>

      {/* Модалка подтверждения сохранения */}
      {confirmOpen && (
        <div
          role="dialog"
          aria-modal="true"
          style={{ position:'fixed', inset:0, zIndex:3500, background:'rgba(0,0,0,.35)', display:'flex', alignItems:'center', justifyContent:'center' }}
          onClick={()=>setConfirmOpen(false)}
        >
          <div
            style={{ background:'var(--card)', border:'1px solid var(--bdr)', borderRadius:16, boxShadow:'var(--shadow)', padding:18, minWidth:320 }}
            onClick={(e)=>e.stopPropagation()}
          >
            <div style={{fontWeight:800, marginBottom:8}}>{isNew ? 'Создать зарубежную недвижимость?' : 'Сохранить изменения?'}</div>
            <div style={{opacity:.8, fontSize:13, marginBottom:12}}>Подтвердите действие или отмените.</div>
            <div style={{display:'flex', gap:8, justifyContent:'flex-end'}}>
              <Btn onClick={()=>setConfirmOpen(false)}>Отмена</Btn>
              <Btn $kind="primary" onClick={()=>{ setConfirmOpen(false); save(); }}>Да, {isNew ? 'создать' : 'сохранить'}</Btn>
            </div>
          </div>
        </div>
      )}

      {/* Остальные секции: скрываем при создании, показываем после сохранения */}
      {isNew ? (
        <Card>
          <div style={{ color:'#777' }}>
            После создания зарубежной недвижимости вы сможете добавить фотографии, удобства, планировки, условия оплаты и назначить менеджеров.
          </div>
        </Card>
      ) : (
        <div style={{display:'grid', gap:12, marginTop:12}}>
          <Collapse title="Условия оплаты" defaultOpen={true}>
            <PaymentPlansPanel propertyId={Number(id)} propertyType="foreign" />
          </Collapse>
          
          <Collapse title="Удобства объекта" defaultOpen={true}>
            <AmenitiesPanel propertyId={Number(id)} propertyType="foreign" />
          </Collapse>
          
          <Collapse title="Планировки" defaultOpen={true}>
            <UnitsPanel propertyId={Number(id)} propertyType="foreign" />
          </Collapse>
          
          <Collapse title="Менеджеры" defaultOpen={true}>
            <ManagersPanel propertyId={Number(id)} propertyType="foreign" />
          </Collapse>
          
          <Collapse title="Фотографии" defaultOpen={true}>
            <PropertyMediaManager propertyId={Number(id)} propertyType="foreign" />
          </Collapse>
        </div>
      )}
    </>
  );
}